import { graphql } from '@@fire-b2b/graphql/codegen'
import type {
  ProductSizesCountFragment,
  ProductSizesFragment,
  ProductSizesWishlistFragment,
} from '@@fire-b2b/graphql/codegen/graphql'

export const PRODUCT_SIZE_BASE_FRAGMENT = graphql(`
  fragment ProductSizeFields on ProductSize {
    shopProductId
    sku
    sizeId
    title
    status
    sortOrder
    sizeGridId
    sizeGridTitle
    sizeGridSortOrder
    stock
    stockStatus
  }
`)

export const PRODUCT_SIZES_FRAGMENTS = graphql(`
  fragment ProductSizes on Product {
    id
    shopProductId
    productSizes {
      ...ProductSizeFields
    }
  }

  fragment ProductSizesCount on Product {
    childrenCount
  }
`)

export const WISHLIST_PRODUCT_SIZES_FRAGMENTS = graphql(`
  fragment ProductSizesWishlist on WishlistPosition {
    id
    productSizes {
      ...ProductSizeFields
    }
  }
`)

export type ProductSizeGrid = {
  shopProductId: number
  sizeGridId: number
  sizeGridTitle: string
  sizeGridSortOrder: number
  sizes: ProductSize[]
}

export type ProductSize = {
  id: number
  shopProductId: number
  sku: string
  sizeId: number
  title: string
  disabled: boolean
  sortOrder: number
  stock?: number
  stockStatus?: number
}

export function mapProductSizeGrids(
  fragment?: ProductSizesFragment | ProductSizesWishlistFragment | null | undefined,
): ProductSizeGrid[] {
  if (!fragment?.productSizes?.length) {
    return []
  }

  const shopProductId = 'shopProductId' in fragment ? (fragment?.shopProductId ?? -1) : -1
  const sizeGridsMap: Map<number, ProductSizeGrid> = new Map()

  for (const size of fragment.productSizes) {
    if (!size || !size.sizeGridId || !size.sizeId) {
      continue
    }

    let sizeGrid = sizeGridsMap.get(size.sizeGridId)

    if (!sizeGrid) {
      sizeGrid = {
        shopProductId,
        sizeGridId: size.sizeGridId,
        sizeGridTitle: size.sizeGridTitle ?? '',
        sizeGridSortOrder: size.sizeGridSortOrder ?? 0,
        sizes: [],
      }

      sizeGridsMap.set(size.sizeGridId, sizeGrid)
    }

    const productSize: ProductSize = {
      id: size.shopProductId ?? -1,
      shopProductId: size.shopProductId ?? -1,
      sizeId: size.sizeId ?? -1,
      title: size.title ?? '',
      disabled: size.status !== 1,
      sortOrder: size.sortOrder ?? 0,
      sku: size.sku ?? '',
      stock: size.stock ?? undefined,
      stockStatus: size.stockStatus ?? undefined,
    }

    sizeGrid.sizes.push(productSize)
  }

  for (const sizeGrid of sizeGridsMap.values()) {
    sizeGrid.sizes.sort((a, b) => a.sortOrder - b.sortOrder)
  }

  return Array.from(sizeGridsMap.values()).sort((a, b) => a.sizeGridSortOrder - b.sizeGridSortOrder)
}

export function mapProductSizesCount(fragment?: ProductSizesCountFragment | null): number {
  return fragment?.childrenCount ?? 0
}
